import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Play, X, ChevronLeft, ChevronRight, Volume2, VolumeX, List, Share2, Clock } from "lucide-react";
import { API, apiService } from "../utils/api";
import { extractYouTubeVideoId } from "../components/shared/VideoPlayer";
import { WatchlistButton, FavoriteButton } from "../components/user";
import { QuickShareButton } from "../components/social";

const ContinueWatchingSection = ({ onPlayContent }) => {
  const [watchHistory, setWatchHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchWatchHistory();
  }, []);

  const fetchWatchHistory = async () => {
    try {
      const userId = localStorage.getItem('cbxtv_user_id') || 'anonymous';
      const response = await axios.get(`${API}/watch-history`, {
        params: { user_id: userId }
      });
      setWatchHistory(response.data.continue_watching || []);
    } catch (error) {
      console.error('Error fetching watch history:', error);
    }
    setLoading(false);
  };

  const handleRemove = async (contentId, e) => {
    e.stopPropagation();
    const userId = localStorage.getItem('cbxtv_user_id') || 'anonymous';
    try {
      await axios.delete(`${API}/watch-history/${contentId}`, {
        params: { user_id: userId }
      });
      setWatchHistory(prev => prev.filter(item => item.content_id !== contentId));
    } catch (error) {
      console.error('Error removing from history:', error);
    }
  };

  if (loading || watchHistory.length === 0) {
    return null;
  }

  return (
    <section className="mb-8" data-testid="continue-watching">
      <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
        <Clock size={20} className="text-red-500" />
        Continue Watching
      </h2>
      <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide">
        {watchHistory.map((item) => (
          <div
            key={item.id}
            className="flex-shrink-0 w-64 bg-gray-900 rounded-lg overflow-hidden cursor-pointer group"
            onClick={() => onPlayContent && onPlayContent(item)}
          >
            <div className="relative aspect-video">
              {item.thumbnail ? (
                <img src={item.thumbnail} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-gray-800 flex items-center justify-center text-3xl">
                  {item.content_type === 'episode' ? '📺' : '🎬'}
                </div>
              )}
              {/* Progress Bar */}
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-gray-700">
                <div 
                  className="h-full bg-red-600"
                  style={{ width: `${item.progress}%` }}
                />
              </div>
              {/* Play Overlay */}
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                <Play size={40} fill="white" className="text-white" />
              </div>
              {/* Remove Button */}
              <button
                onClick={(e) => handleRemove(item.content_id, e)}
                className="absolute top-2 right-2 bg-black/70 hover:bg-red-600 rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X size={16} className="text-white" />
              </button>
            </div>
            <div className="p-3">
              <h4 className="text-white font-medium truncate text-sm">
                {item.show_title ? `${item.show_title}` : item.title}
              </h4>
              {item.show_title && (
                <p className="text-gray-500 text-xs">
                  S{item.season}E{item.episode_number}: {item.title}
                </p>
              )}
              <p className="text-gray-500 text-xs mt-1">
                {Math.floor(item.current_time / 60)}:{String(item.current_time % 60).padStart(2, '0')} / {Math.floor(item.duration / 60)}:{String(item.duration % 60).padStart(2, '0')}
              </p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

const NetflixPlayer = ({ content, onClose }) => {
  const [selectedEpisode, setSelectedEpisode] = useState(null);
  const [selectedSeason, setSelectedSeason] = useState(1);
  const [isPlaying, setIsPlaying] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [showEpisodes, setShowEpisodes] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [buffered, setBuffered] = useState(0);
  
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const controlsTimeoutRef = useRef(null);

  // Auto-select first episode for TV shows
  useEffect(() => {
    if (content.contentType === 'tvshow' && content.episodes?.length > 0 && !selectedEpisode) {
      setSelectedEpisode(content.episodes[0]);
    }
  }, [content]);

  // Hide controls after inactivity
  useEffect(() => {
    const hideControls = () => {
      if (isPlaying) {
        setShowControls(false);
      }
    };

    if (showControls) {
      clearTimeout(controlsTimeoutRef.current);
      controlsTimeoutRef.current = setTimeout(hideControls, 3000);
    }

    return () => clearTimeout(controlsTimeoutRef.current);
  }, [showControls, isPlaying]);

  const handleMouseMove = () => {
    setShowControls(true);
  };

  // Save watch progress
  const saveProgress = async () => {
    if (!videoRef.current || !duration) return;
    
    const video = videoRef.current;
    const progress = (video.currentTime / video.duration) * 100;
    const userId = localStorage.getItem('cbxtv_user_id') || 'anonymous';
    
    if (!localStorage.getItem('cbxtv_user_id')) {
      const newUserId = `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('cbxtv_user_id', newUserId);
    }
    
    const isEpisode = content.contentType === 'tvshow' && selectedEpisode;
    
    try {
      await axios.post(`${API}/watch-history`, null, {
        params: {
          content_id: isEpisode ? selectedEpisode.id : content.id,
          content_type: isEpisode ? 'episode' : 'movie',
          progress: Math.round(progress),
          duration: Math.round(video.duration),
          current_time: Math.round(video.currentTime),
          user_id: userId,
          title: isEpisode ? selectedEpisode.title : content.title,
          thumbnail: isEpisode ? selectedEpisode.thumbnail : (content.poster || content.thumbnail),
          show_id: isEpisode ? content.id : null,
          show_title: isEpisode ? content.title : null,
          season: isEpisode ? selectedEpisode.season : null,
          episode_number: isEpisode ? selectedEpisode.episode_number : null
        }
      });
    } catch (error) {
      console.error('Error saving progress:', error);
    }
  };

  useEffect(() => {
    const interval = setInterval(saveProgress, 10000);
    return () => clearInterval(interval);
  }, [selectedEpisode, content, duration]);

  const handleClose = () => {
    saveProgress();
    onClose();
  };

  // Get video URL
  const getVideoUrl = () => {
    if (content.contentType === 'movie') {
      return content.video_url || content.youtube_url;
    }
    if (selectedEpisode) {
      return selectedEpisode.video_url || selectedEpisode.youtube_url;
    }
    const firstEp = content.episodes?.[0];
    return firstEp?.video_url || firstEp?.youtube_url || content.video_url || content.youtube_url;
  };

  const videoUrl = getVideoUrl();
  const youtubeId = extractYouTubeVideoId(videoUrl);

  // Video controls
  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleSeek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    const newTime = percent * duration;
    if (videoRef.current) {
      videoRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (videoRef.current) {
      videoRef.current.volume = newVolume;
    }
    setIsMuted(newVolume === 0);
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  const skip = (seconds) => {
    if (videoRef.current) {
      videoRef.current.currentTime += seconds;
    }
  };

  // Format time
  const formatTime = (time) => {
    const hours = Math.floor(time / 3600);
    const mins = Math.floor((time % 3600) / 60);
    const secs = Math.floor(time % 60);
    if (hours > 0) {
      return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Get next episode
  const getNextEpisode = () => {
    if (!selectedEpisode || !content.episodes) return null;
    const currentIndex = content.episodes.findIndex(ep => ep.id === selectedEpisode.id);
    return content.episodes[currentIndex + 1] || null;
  };

  const nextEpisode = getNextEpisode();

  // Episodes by season
  const episodesBySeason = content.episodes?.reduce((acc, ep) => {
    const season = ep.season || 1;
    if (!acc[season]) acc[season] = [];
    acc[season].push(ep);
    return acc;
  }, {}) || {};

  const seasons = Object.keys(episodesBySeason).map(Number).sort((a, b) => a - b);

  // Handle video ended
  const handleVideoEnded = () => {
    saveProgress();
    if (nextEpisode) {
      setSelectedEpisode(nextEpisode);
    }
  };

  return (
    <div 
      ref={containerRef}
      className="fixed inset-0 bg-black z-50 cursor-none"
      onMouseMove={handleMouseMove}
      onClick={() => !showEpisodes && togglePlay()}
      data-testid="netflix-player"
    >
      {/* Video */}
      {youtubeId ? (
        <div className="w-full h-full">
          <iframe
            src={`https://www.youtube.com/embed/${youtubeId}?autoplay=1&controls=0&modestbranding=1&rel=0&showinfo=0`}
            className="w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      ) : videoUrl ? (
        <video
          ref={videoRef}
          src={videoUrl}
          className="w-full h-full object-contain"
          autoPlay
          onClick={(e) => e.stopPropagation()}
          onTimeUpdate={() => {
            if (videoRef.current) {
              setCurrentTime(videoRef.current.currentTime);
              if (videoRef.current.buffered.length > 0) {
                setBuffered(videoRef.current.buffered.end(videoRef.current.buffered.length - 1));
              }
            }
          }}
          onLoadedMetadata={() => {
            if (videoRef.current) {
              setDuration(videoRef.current.duration);
            }
          }}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          onEnded={handleVideoEnded}
        />
      ) : (
        <div className="w-full h-full flex flex-col items-center justify-center">
          <Play size={80} className="text-red-600 mb-4" />
          <p className="text-gray-400 text-xl">No video available</p>
        </div>
      )}

      {/* Controls Overlay */}
      <div 
        className={`absolute inset-0 transition-opacity duration-300 ${showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        style={{ cursor: showControls ? 'default' : 'none' }}
      >
        {/* Top Gradient */}
        <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-black/80 to-transparent" />
        
        {/* Bottom Gradient */}
        <div className="absolute bottom-0 left-0 right-0 h-48 bg-gradient-to-t from-black/90 to-transparent" />

        {/* Back Button */}
        <button
          onClick={(e) => { e.stopPropagation(); handleClose(); }}
          className="absolute top-6 left-6 flex items-center gap-2 text-white hover:text-gray-300 transition-colors"
        >
          <ChevronLeft size={32} />
          <span className="text-lg font-medium">Back to Browse</span>
        </button>

        {/* Action Buttons (Top Center) */}
        <div className="absolute top-6 left-1/2 -translate-x-1/2 flex items-center gap-3">
          <WatchlistButton contentId={content.id} variant="icon" />
          <FavoriteButton contentId={content.id} variant="icon" />
          <QuickShareButton 
            contentType={content.contentType || 'movie'} 
            contentId={content.id} 
            contentTitle={content.title} 
          />
        </div>

        {/* Title (Top Right) */}
        <div className="absolute top-6 right-6 text-right">
          <h1 className="text-white text-xl font-bold">{content.title}</h1>
          {selectedEpisode && (
            <p className="text-gray-300 text-sm">
              S{selectedEpisode.season}:E{selectedEpisode.episode_number} "{selectedEpisode.title}"
            </p>
          )}
        </div>

        {/* Center Play/Pause */}
        {!isPlaying && (
          <div className="absolute inset-0 flex items-center justify-center">
            <button
              onClick={(e) => { e.stopPropagation(); togglePlay(); }}
              className="bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full p-6 transition-colors"
            >
              <Play size={64} fill="white" className="text-white ml-2" />
            </button>
          </div>
        )}

        {/* Bottom Controls */}
        <div className="absolute bottom-0 left-0 right-0 px-6 pb-6">
          {/* Progress Bar */}
          <div 
            className="group relative h-1 hover:h-2 bg-gray-600/50 rounded-full cursor-pointer mb-4 transition-all"
            onClick={(e) => { e.stopPropagation(); handleSeek(e); }}
          >
            {/* Buffered */}
            <div 
              className="absolute top-0 left-0 h-full bg-gray-500/50 rounded-full"
              style={{ width: `${(buffered / duration) * 100}%` }}
            />
            {/* Progress */}
            <div 
              className="absolute top-0 left-0 h-full bg-red-600 rounded-full"
              style={{ width: `${(currentTime / duration) * 100}%` }}
            />
            {/* Thumb */}
            <div 
              className="absolute top-1/2 -translate-y-1/2 w-4 h-4 bg-red-600 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
              style={{ left: `calc(${(currentTime / duration) * 100}% - 8px)` }}
            />
          </div>

          {/* Controls Row */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {/* Play/Pause */}
              <button
                onClick={(e) => { e.stopPropagation(); togglePlay(); }}
                className="text-white hover:text-gray-300 transition-colors"
              >
                {isPlaying ? (
                  <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
                    <rect x="6" y="4" width="4" height="16" />
                    <rect x="14" y="4" width="4" height="16" />
                  </svg>
                ) : (
                  <Play size={32} fill="currentColor" />
                )}
              </button>

              {/* Skip Backward */}
              <button
                onClick={(e) => { e.stopPropagation(); skip(-10); }}
                className="text-white hover:text-gray-300 transition-colors"
              >
                <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12.5 8L8 12l4.5 4" />
                  <path d="M4 12a8 8 0 1 0 8-8" />
                  <text x="12" y="14" fontSize="6" fill="currentColor" stroke="none">10</text>
                </svg>
              </button>

              {/* Skip Forward */}
              <button
                onClick={(e) => { e.stopPropagation(); skip(10); }}
                className="text-white hover:text-gray-300 transition-colors"
              >
                <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M11.5 8L16 12l-4.5 4" />
                  <path d="M20 12a8 8 0 1 1-8-8" />
                  <text x="8" y="14" fontSize="6" fill="currentColor" stroke="none">10</text>
                </svg>
              </button>

              {/* Volume */}
              <div className="flex items-center gap-2 group/volume">
                <button
                  onClick={(e) => { e.stopPropagation(); toggleMute(); }}
                  className="text-white hover:text-gray-300 transition-colors"
                >
                  {isMuted || volume === 0 ? (
                    <VolumeX size={28} />
                  ) : (
                    <Volume2 size={28} />
                  )}
                </button>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={isMuted ? 0 : volume}
                  onChange={(e) => { e.stopPropagation(); handleVolumeChange(e); }}
                  onClick={(e) => e.stopPropagation()}
                  className="w-0 group-hover/volume:w-24 transition-all duration-200 accent-red-600"
                />
              </div>

              {/* Time */}
              <span className="text-white text-sm font-medium">
                {formatTime(currentTime)} / {formatTime(duration)}
              </span>
            </div>

            <div className="flex items-center gap-4">
              {/* Episode Title */}
              {selectedEpisode && (
                <span className="text-white text-sm hidden md:block">
                  {selectedEpisode.title}
                </span>
              )}

              {/* Next Episode */}
              {nextEpisode && (
                <button
                  onClick={(e) => { e.stopPropagation(); setSelectedEpisode(nextEpisode); }}
                  className="flex items-center gap-2 bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded transition-colors"
                >
                  <span className="text-sm">Next Episode</span>
                  <ChevronRight size={20} />
                </button>
              )}

              {/* Episodes Button (for TV Shows) */}
              {content.contentType === 'tvshow' && content.episodes?.length > 0 && (
                <button
                  onClick={(e) => { e.stopPropagation(); setShowEpisodes(!showEpisodes); }}
                  className="text-white hover:text-gray-300 transition-colors"
                >
                  <List size={28} />
                </button>
              )}

              {/* Fullscreen */}
              <button
                onClick={(e) => { e.stopPropagation(); toggleFullscreen(); }}
                className="text-white hover:text-gray-300 transition-colors"
              >
                {isFullscreen ? (
                  <svg className="w-7 h-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3" />
                  </svg>
                ) : (
                  <svg className="w-7 h-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Episodes Sidebar */}
      {showEpisodes && (
        <div 
          className="absolute top-0 right-0 w-96 h-full bg-black/95 overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-white text-xl font-bold">Episodes</h2>
              <button
                onClick={() => setShowEpisodes(false)}
                className="text-gray-400 hover:text-white"
              >
                <X size={24} />
              </button>
            </div>

            {/* Season Selector */}
            {seasons.length > 1 && (
              <div className="flex gap-2 mb-4 flex-wrap">
                {seasons.map(season => (
                  <button
                    key={season}
                    onClick={() => setSelectedSeason(season)}
                    className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
                      selectedSeason === season 
                        ? 'bg-white text-black' 
                        : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                    }`}
                  >
                    Season {season}
                  </button>
                ))}
              </div>
            )}

            {/* Episodes List */}
            <div className="space-y-3">
              {(episodesBySeason[selectedSeason] || []).map((episode, index) => (
                <button
                  key={episode.id}
                  onClick={() => { setSelectedEpisode(episode); setShowEpisodes(false); }}
                  className={`w-full text-left rounded-lg overflow-hidden transition-colors ${
                    selectedEpisode?.id === episode.id 
                      ? 'bg-gray-700' 
                      : 'bg-gray-900 hover:bg-gray-800'
                  }`}
                >
                  <div className="flex gap-3 p-3">
                    <div className="relative w-28 h-16 flex-shrink-0 rounded overflow-hidden bg-gray-800">
                      {episode.thumbnail ? (
                        <img src={episode.thumbnail} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Play size={24} className="text-gray-600" />
                        </div>
                      )}
                      {selectedEpisode?.id === episode.id && (
                        <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                          <div className="w-8 h-8 border-2 border-white rounded-full flex items-center justify-center">
                            <Play size={16} fill="white" className="text-white ml-0.5" />
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-medium mb-1">
                        {episode.episode_number}. {episode.title}
                      </p>
                      <p className="text-gray-500 text-sm">{episode.duration}m</p>
                    </div>
                  </div>
                  {episode.description && (
                    <p className="text-gray-400 text-sm px-3 pb-3 line-clamp-2">
                      {episode.description}
                    </p>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Alias for backward compatibility
const ContentPlayerModal = NetflixPlayer;


const ContentLibraryPage = () => {
  const [movies, setMovies] = useState([]);
  const [tvShows, setTVShows] = useState([]);
  const [selectedContent, setSelectedContent] = useState(null);
  const [activeTab, setActiveTab] = useState('movies');
  const [selectedGenre, setSelectedGenre] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const genres = ['Action', 'Comedy', 'Drama', 'Horror', 'Thriller', 'Sci-Fi', 'Romance', 'Documentary'];

  useEffect(() => {
    fetchContent();
  }, [selectedGenre]);

  const fetchContent = async () => {
    setLoading(true);
    try {
      const [moviesRes, showsRes] = await Promise.all([
        apiService.getMovies(selectedGenre),
        apiService.getTVShows(selectedGenre)
      ]);
      setMovies(moviesRes.data || []);
      setTVShows(showsRes.data || []);
    } catch (error) {
      console.error('Error fetching content:', error);
    }
    setLoading(false);
  };

  const handlePlayContent = (content, type) => {
    setSelectedContent({ ...content, contentType: type });
  };

  const handleContinueWatching = async (item) => {
    // Fetch the full content to play
    try {
      if (item.content_type === 'movie') {
        const response = await apiService.getMovie(item.content_id);
        setSelectedContent({ ...response.data, contentType: 'movie' });
      } else if (item.content_type === 'episode' && item.show_id) {
        const response = await apiService.getTVShow(item.show_id);
        setSelectedContent({ ...response.data, contentType: 'tvshow' });
      }
    } catch (error) {
      console.error('Error loading content:', error);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] pt-44 px-4 md:px-12" data-testid="content-library-page">
      {/* Continue Watching */}
      <ContinueWatchingSection onPlayContent={handleContinueWatching} />

      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-2">
          <span className="text-red-600">CBX</span>TV Library
        </h1>
        <p className="text-gray-400">Watch movies, TV shows, and exclusive content</p>
      </div>

      {/* Tab Navigation */}
      <div className="flex flex-wrap gap-2 mb-6">
        <button
          onClick={() => setActiveTab('movies')}
          className={`px-6 py-3 rounded-lg font-medium transition-colors ${
            activeTab === 'movies' ? 'bg-red-600 text-white' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
          }`}
        >
          🎬 Movies ({movies.length})
        </button>
        <button
          onClick={() => setActiveTab('tvshows')}
          className={`px-6 py-3 rounded-lg font-medium transition-colors ${
            activeTab === 'tvshows' ? 'bg-red-600 text-white' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
          }`}
        >
          📺 TV Shows ({tvShows.length})
        </button>
      </div>

      {/* Genre Filter */}
      <div className="flex flex-wrap gap-2 mb-8">
        <button
          onClick={() => setSelectedGenre(null)}
          className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
            !selectedGenre ? 'bg-red-600 text-white' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
          }`}
        >
          All
        </button>
        {genres.map(genre => (
          <button
            key={genre}
            onClick={() => setSelectedGenre(genre)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              selectedGenre === genre ? 'bg-red-600 text-white' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
            }`}
          >
            {genre}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-white">Loading...</div>
        </div>
      ) : (
        <>
          {/* Movies Tab */}
          {activeTab === 'movies' && (
            <div>
              {movies.length === 0 ? (
                <div className="text-center py-16">
                  <div className="text-6xl mb-4">🎬</div>
                  <h3 className="text-white text-xl font-bold mb-2">No Movies Yet</h3>
                  <p className="text-gray-400">Movies will appear here once added by admins</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                  {movies.map(movie => (
                    <div 
                      key={movie.id} 
                      className="group cursor-pointer"
                      data-testid="movie-card"
                    >
                      <div className="relative aspect-[2/3] rounded-lg overflow-hidden bg-gray-800 mb-2">
                        {movie.poster || movie.thumbnail ? (
                          <img 
                            src={movie.poster || movie.thumbnail} 
                            alt={movie.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                            onClick={() => handlePlayContent(movie, 'movie')}
                            onError={(e) => { e.target.style.display = 'none'; e.target.parentNode.innerHTML = '<div class="w-full h-full flex items-center justify-center text-4xl bg-gradient-to-br from-gray-800 to-gray-900">🎬</div>'; }}
                          />
                        ) : (
                          <div 
                            className="w-full h-full flex items-center justify-center text-4xl bg-gradient-to-br from-gray-800 to-gray-900"
                            onClick={() => handlePlayContent(movie, 'movie')}
                          >
                            🎬
                          </div>
                        )}
                        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-3">
                          <button 
                            className="bg-red-600 hover:bg-red-700 rounded-full p-4"
                            onClick={() => handlePlayContent(movie, 'movie')}
                          >
                            <Play size={32} fill="white" className="text-white ml-1" />
                          </button>
                          {/* Action Buttons */}
                          <div className="flex gap-2">
                            <WatchlistButton contentId={movie.id} variant="icon" />
                            <FavoriteButton contentId={movie.id} variant="icon" />
                          </div>
                        </div>
                        {movie.duration && (
                          <div className="absolute bottom-2 right-2 bg-black/80 px-2 py-1 rounded text-xs text-white">
                            {movie.duration} min
                          </div>
                        )}
                      </div>
                      <h4 className="text-white font-medium truncate group-hover:text-red-400">{movie.title}</h4>
                      <p className="text-gray-500 text-sm">{movie.year} • {movie.genre}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* TV Shows Tab */}
          {activeTab === 'tvshows' && (
            <div>
              {tvShows.length === 0 ? (
                <div className="text-center py-16">
                  <div className="text-6xl mb-4">📺</div>
                  <h3 className="text-white text-xl font-bold mb-2">No TV Shows Yet</h3>
                  <p className="text-gray-400">TV Shows will appear here once added by admins</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {tvShows.map(show => (
                    <div 
                      key={show.id} 
                      className="bg-gray-900 rounded-xl overflow-hidden border border-gray-800 hover:border-red-600 transition-colors cursor-pointer group"
                      data-testid="show-card"
                    >
                      <div className="flex">
                        <div 
                          className="w-32 h-48 flex-shrink-0"
                          onClick={() => handlePlayContent(show, 'tvshow')}
                        >
                          {show.poster || show.thumbnail ? (
                            <img 
                              src={show.poster || show.thumbnail} 
                              alt={show.title}
                              className="w-full h-full object-cover"
                              onError={(e) => { e.target.style.display = 'none'; e.target.parentNode.innerHTML = '<div class="w-full h-full flex items-center justify-center text-3xl bg-gray-800">📺</div>'; }}
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-3xl bg-gray-800">
                              📺
                            </div>
                          )}
                        </div>
                        <div className="flex-1 p-4" onClick={() => handlePlayContent(show, 'tvshow')}>
                          <h4 className="text-white font-bold text-lg mb-1">{show.title}</h4>
                          <p className="text-gray-400 text-sm mb-2">{show.genre} • {show.year} • {show.rating}</p>
                          <p className="text-gray-500 text-sm line-clamp-2 mb-3">{show.description}</p>
                          <div className="flex items-center gap-2">
                            <span className={`text-xs px-2 py-1 rounded ${
                              show.status === 'ongoing' ? 'bg-green-600/20 text-green-400' :
                              show.status === 'ended' ? 'bg-gray-600/20 text-gray-400' :
                              'bg-yellow-600/20 text-yellow-400'
                            }`}>
                              {show.status}
                            </span>
                            <span className="text-gray-500 text-xs">
                              {show.seasons} Season{show.seasons > 1 ? 's' : ''} • {show.episodes?.length || 0} Episodes
                            </span>
                          </div>
                        </div>
                      </div>
                      {/* Action Buttons */}
                      <div className="flex items-center gap-2 px-4 py-3 border-t border-gray-800 bg-gray-900/50 opacity-0 group-hover:opacity-100 transition-opacity">
                        <WatchlistButton contentId={show.id} variant="icon" />
                        <FavoriteButton contentId={show.id} variant="icon" />
                        <div className="ml-auto">
                          <button 
                            onClick={(e) => { e.stopPropagation(); handlePlayContent(show, 'tvshow'); }}
                            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2"
                          >
                            <Play size={16} fill="white" /> Watch
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Video Player Modal */}
      {selectedContent && (
        <ContentPlayerModal 
          content={selectedContent}
          onClose={() => setSelectedContent(null)}
        />
      )}
    </div>
  );
};

export default ContentLibraryPage;
export { NetflixPlayer, ContentPlayerModal, ContinueWatchingSection };
